import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getAuthHeader } from '../services/tokenService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function usePropositionsSignees() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pending_validation: 0, validated: 0, refused: 0, awaiting_signature: 0 });
  const [filter, setFilter] = useState('pending_validation');
  const [loading, setLoading] = useState(true);
  const [relancingId, setRelancingId] = useState(null);

  const [refuserOpen, setRefuserOpen] = useState(false);
  const [refuserTarget, setRefuserTarget] = useState(null);
  const [refuserRaison, setRefuserRaison] = useState('');
  const [refuserSubmitting, setRefuserSubmitting] = useState(false);

  const [validerOpen, setValiderOpen] = useState(false);
  const [validerTarget, setValiderTarget] = useState(null);
  const [validerNote, setValiderNote] = useState('');
  const [validerSubmitting, setValiderSubmitting] = useState(false);

  const [pdfLoadingId, setPdfLoadingId] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [resendOpen, setResendOpen] = useState(false);
  const [resendSending, setResendSending] = useState(false);
  const [resendForm, setResendForm] = useState({ to_email: '', message: '' });
  const [emailLog, setEmailLog] = useState(null);
  const [emailLogLoadingId, setEmailLogLoadingId] = useState(null);

  const fetchAll = useCallback(async (statusToFetch = filter) => {
    setLoading(true);
    try {
      const [listRes, countsRes] = await Promise.all([
        axios.get(`${API}/admin/propositions-signees`, {
          headers: getAuthHeader(),
          params: { status: statusToFetch, limit: 200 },
        }),
        axios.get(`${API}/admin/propositions-signees/counts`, { headers: getAuthHeader() }),
      ]);
      setItems(listRes.data?.items || []);
      setCounts(countsRes.data || {});
    } catch {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchAll(filter); }, [filter, fetchAll]);

  const openRefuser = (item) => {
    setRefuserTarget(item);
    setRefuserRaison('');
    setRefuserOpen(true);
  };

  const submitRefuser = async () => {
    if (refuserRaison.trim().length < 3) {
      toast.error('Raison requise (min. 3 caractères)');
      return;
    }
    setRefuserSubmitting(true);
    try {
      await axios.post(
        `${API}/admin/propositions-signees/${encodeURIComponent(refuserTarget.devis_id)}/refuser`,
        { raison: refuserRaison.trim() },
        { headers: getAuthHeader() },
      );
      toast.success('Proposition refusée. Le signataire a été notifié.');
      setRefuserOpen(false);
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setRefuserSubmitting(false);
    }
  };

  const openValider = (item) => {
    setValiderTarget(item);
    setValiderNote('');
    setValiderOpen(true);
  };

  const submitValider = async (then) => {
    setValiderSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/admin/propositions-signees/${encodeURIComponent(validerTarget.devis_id)}/valider`,
        { note: (validerNote || '').trim() || undefined },
        { headers: getAuthHeader() },
      );
      toast.success('Proposition validée');
      setValiderOpen(false);
      if (then === 'goto' && res.data?.next_action_url) {
        window.open(res.data.next_action_url, '_blank');
      }
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setValiderSubmitting(false);
    }
  };

  const handleRelancer = async (item) => {
    setRelancingId(item.devis_id);
    try {
      const res = await axios.post(
        `${API}/admin/propositions-signees/${encodeURIComponent(item.devis_id)}/relancer`,
        {},
        { headers: getAuthHeader() },
      );
      toast.success(`Relance #${res.data.relance_index} envoyée à ${res.data.to_email}`);
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la relance');
    } finally {
      setRelancingId(null);
    }
  };

  const fetchSignedPdfBlob = async (item, mode = 'view') => {
    const url = `${API}/admin/propositions-signees/${encodeURIComponent(item.devis_id)}/download-pdf?mode=${mode}`;
    const res = await axios.get(url, {
      headers: getAuthHeader(),
      responseType: 'blob',
    });
    return new Blob([res.data], { type: 'application/pdf' });
  };

  const handleViewPdf = async (item) => {
    setPdfLoadingId(item.devis_id);
    try {
      const blob = await fetchSignedPdfBlob(item, 'view');
      const blobUrl = URL.createObjectURL(blob);
      const fileName = `Proposition_signee_${item.devis_id}.pdf`;
      setPdfPreview({ blobUrl, fileName, devis_id: item.devis_id, item });
    } catch (e) {
      toast.error(e?.response?.status === 404 ? 'PDF signé introuvable' : (e?.response?.data?.detail || 'Erreur lors du chargement du PDF'));
    } finally {
      setPdfLoadingId(null);
    }
  };

  const handleDownloadPdf = async (item) => {
    setPdfLoadingId(item.devis_id);
    try {
      const blob = await fetchSignedPdfBlob(item, 'download');
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `Proposition_signee_${item.devis_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
      toast.success('Téléchargement lancé');
    } catch (e) {
      toast.error(e?.response?.status === 404 ? 'PDF signé introuvable' : (e?.response?.data?.detail || 'Erreur lors du téléchargement'));
    } finally {
      setPdfLoadingId(null);
    }
  };

  const closePdfPreview = () => {
    if (pdfPreview?.blobUrl) URL.revokeObjectURL(pdfPreview.blobUrl);
    setPdfPreview(null);
    setResendOpen(false);
    setResendForm({ to_email: '', message: '' });
  };

  const handleResendPdfByEmail = async () => {
    if (!pdfPreview?.item?.devis_id) return;
    setResendSending(true);
    try {
      const payload = {
        to_email: (resendForm.to_email || '').trim() || undefined,
        message: (resendForm.message || '').trim() || undefined,
      };
      const res = await axios.post(
        `${API}/admin/propositions-signees/${encodeURIComponent(pdfPreview.item.devis_id)}/resend-signed-pdf`,
        payload,
        { headers: getAuthHeader() },
      );
      toast.success(`PDF renvoye a ${res.data.to_email}`);
      setResendOpen(false);
      setResendForm({ to_email: '', message: '' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Echec de l\'envoi');
    } finally {
      setResendSending(false);
    }
  };

  const handleShowEmailLog = async (item) => {
    setEmailLogLoadingId(item.devis_id);
    try {
      const res = await axios.get(
        `${API}/admin/propositions-signees/${encodeURIComponent(item.devis_id)}/email-log`,
        { headers: getAuthHeader() },
      );
      setEmailLog(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur chargement journal emails');
    } finally {
      setEmailLogLoadingId(null);
    }
  };

  return {
    items, counts, filter, setFilter, loading, relancingId,
    refuserOpen, setRefuserOpen, refuserTarget, refuserRaison, setRefuserRaison, refuserSubmitting,
    validerOpen, setValiderOpen, validerTarget, validerNote, setValiderNote, validerSubmitting,
    pdfLoadingId, pdfPreview, resendOpen, setResendOpen, resendSending, resendForm, setResendForm,
    emailLog, setEmailLog, emailLogLoadingId,
    fetchAll, openRefuser, submitRefuser, openValider, submitValider, handleRelancer,
    handleViewPdf, handleDownloadPdf, closePdfPreview, handleResendPdfByEmail, handleShowEmailLog,
  };
}
